import i18n from "@/i18n/i18n";
import { type BrandComparisonData } from "@/services/mock";
import type { BrandSearchItem } from "@/types/api";
import {
  brandAiMetricsTrendApi,
  type BrandAiTrendPureDict,
} from "./brandAiMetricsTrend";
import { brandComparisonApi } from "./brandComparison";
import {
  brandMetricsRatesApi,
  type BrandMetricsRates,
} from "./brandMetricsRates";
import { type BrandMetricsTrendPoint } from "./brandMetricsTrend";
import brandRatesApi, { type BrandRatesItem } from "./BrandRates";
import { contentCiteApi, type ContentCiteItem } from "./contentCite";
import { mediaCiteApi, type MediaCiteItem } from "./mediaCite";
import { modelsApi, type AIModel } from "./models";
import reputationApi, { type ReputationKeywordsResponse } from "./reputation";
import { searchApi } from "./search";

/**
 * 搜索流程结果接口
 */
export interface SearchFlowResult {
  searchResults: BrandSearchItem[]; // 搜索结果（原始API数据）
  models: AIModel[]; // 支持的模型列表
  brandMetrics: BrandMetricsRates | null; // 品牌指标数据
  brandMetricsTrend?: BrandMetricsTrendPoint[]; // 品牌指标趋势（提及率等）
  brandComparison?: BrandComparisonData; // 品牌对比数据
  brandAiMetricsTrend?: BrandAiTrendPureDict; // AI 平台维度的趋势数据（字典结构）
  brandRatesList?: BrandRatesItem[]; // 聚合后的品牌指标列表（TOP10等）
  reputationKeywords?: ReputationKeywordsResponse; // 品牌声誉关键词数据
  mediaCite?: MediaCiteItem[]; // 媒体引用分析数据
  contentCite?: ContentCiteItem[]; // 内容引用分析数据
  error?: string; // 错误信息
}

/**
 * 搜索流程参数接口
 */
export interface SearchFlowParams {
  keyword: string; // 搜索关键词
  brandId?: string; // 品牌ID（从搜索结果中获取）
  categoryId?: string; // 分类ID（从搜索结果中获取）
}

/**
 * 特定品牌项目接口
 */
export interface SpecificBrandItem {
  brandId: string; // 品牌ID
  industryId: string; // 行业ID
  brandName?: string; // 品牌名称
  categoryName?: string; // 分类名称
  days?: number; // 时间范围（天数），默认7天
  modelId?: string; // AI平台选择，默认'all'
  startDate?: string; // 自定义开始日期 (YYYY-MM-DD)
  endDate?: string; // 自定义结束日期 (YYYY-MM-DD)
  qId?: string; // query ID
}

/**
 * 时间范围变更参数接口
 */
export interface TimeRangeChangeParams {
  brandId: string; // 品牌ID
  categoryId: string; // 分类ID
  days: number; // 天数
  modelId?: string; // 模型ID（可选，默认为'all'）
  brandName?: string; // 品牌名称（用于日志）
  categoryName?: string; // 分类名称（用于日志）
  startDate?: string; // 自定义开始日期 (YYYY-MM-DD)
  endDate?: string; // 自定义结束日期 (YYYY-MM-DD)
  qId?: string; // query ID
}

/**
 * 数据刷新结果接口
 */
export interface DataRefreshResult {
  brandMetrics: BrandMetricsRates | null; // 品牌指标数据
  brandMetricsTrend?: BrandMetricsTrendPoint[]; // 品牌指标趋势
  brandAiMetricsTrend?: BrandAiTrendPureDict; // AI 平台维度的趋势数据（字典结构）
  reputationKeywords?: ReputationKeywordsResponse; // 品牌声誉关键词数据
  mediaCite?: MediaCiteItem[]; // 媒体引用分析数据
  contentCite?: ContentCiteItem[]; // 内容引用分析数据
  brandComparison?: BrandComparisonData; // 品牌对比数据
  brandRatesList?: BrandRatesItem[]; // 聚合后的品牌指标列表
  error?: string; // 错误信息
}

/**
 * 搜索流程API
 * 整合搜索、模型列表、品牌指标等API调用
 */
export const searchFlowApi = {
  // 从品牌对比数据中抽取当前品牌的趋势，映射为 BrandMetricsTrendPoint[]
  _deriveTrendFromComparison(
    comparison: BrandComparisonData | undefined | null
  ): BrandMetricsTrendPoint[] | undefined {
    if (
      !comparison ||
      !comparison.brandList?.curBrand?.brandId ||
      !comparison.rateList
    )
      return undefined;
    const curId = comparison.brandList.curBrand.brandId;
    const mr = comparison.rateList.mentionRate?.[curId] || [];
    const fmr = comparison.rateList.firstMentionRate?.[curId] || [];
    const frr = comparison.rateList.firstRecommendationRate?.[curId] || [];
    const pmr = comparison.rateList.positiveMentionRate?.[curId] || [];
    const nmr = comparison.rateList.negativeMentionRate?.[curId] || [];
    if (!mr.length && !fmr.length && !frr.length && !pmr.length && !nmr.length)
      return [];

    // 按天聚合
    const dayMap = new Map<string, Partial<BrandMetricsTrendPoint>>();
    const attach = (
      arr: Array<{ day: string; rate: number }>,
      key: keyof BrandMetricsTrendPoint
    ) => {
      arr.forEach((item) => {
        const ex = dayMap.get(item.day) || { date: item.day };
        (ex as any)[key] = item.rate;
        dayMap.set(item.day, ex);
      });
    };
    attach(mr, "mentionRate");
    attach(fmr, "firstMentionRate");
    attach(frr, "firstRecommendationRate");
    attach(pmr, "positiveMentionRate");
    attach(nmr, "negativeMentionRate");

    // 排序输出
    const points: BrandMetricsTrendPoint[] = Array.from(dayMap.values())
      .map((p) => ({
        date: String(p.date),
        mentionRate: Number((p as any).mentionRate ?? 0),
        firstMentionRate: Number((p as any).firstMentionRate ?? 0),
        firstRecommendationRate: Number(
          (p as any).firstRecommendationRate ?? 0
        ),
        positiveMentionRate:
          (p as any).positiveMentionRate !== undefined
            ? Number((p as any).positiveMentionRate)
            : undefined,
        negativeMentionRate:
          (p as any).negativeMentionRate !== undefined
            ? Number((p as any).negativeMentionRate)
            : undefined,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return points;
  },
  /**
   * 执行完整的搜索流程（重构版本）
   * 支持两种模式：
   * 1. 关键词搜索模式：执行搜索 → 取第一个结果
   * 2. 指定品牌模式：直接使用指定的品牌信息
   * @param keyword 搜索关键词
   * @param specificItem 可选的特定品牌项目（如果提供，将跳过搜索直接使用）
   * @returns Promise<SearchFlowResult> 搜索结果，包含重置信息
   */
  async executeSearchFlow(
    keyword: string,
    specificItem?: SpecificBrandItem
  ): Promise<SearchFlowResult> {
    console.log("🚀 开始执行搜索流程:", { keyword, specificItem });

    // 提取参数（如果没有指定则使用默认值）
    const days = specificItem?.days || 7;
    const modelId = specificItem?.modelId || "all";
    const startDate = specificItem?.startDate;
    const endDate = specificItem?.endDate;
    const qId = specificItem?.qId || "";

    console.log("📊 使用参数:", { days, modelId, startDate, endDate, qId });

    try {
      // 第一步：确定要使用的品牌信息
      let brandInfo: {
        brandId: string;
        categoryId: string;
        brandName?: string;
        categoryName?: string;
      };
      let searchResults: BrandSearchItem[] = [];

      if (specificItem?.brandId && specificItem?.industryId) {
        // 指定品牌模式：直接使用传入的品牌信息
        console.log("🎯 使用指定品牌模式:", specificItem);
        brandInfo = {
          brandId: specificItem.brandId,
          categoryId: specificItem.industryId,
          brandName: specificItem.brandName,
          categoryName: specificItem.categoryName,
        };
        searchResults = []; // 指定品牌模式不需要搜索结果
      } else {
        // 关键词搜索模式：先搜索获取品牌信息
        console.log("🔍 使用关键词搜索模式:", keyword);
        const searchResult = await searchApi.search(keyword);
        console.log("搜索结果:", searchResult);

        if (searchResult.items && searchResult.items.length > 0) {
          const firstResult = searchResult.items[0];
          const firstRawResult = searchResult.rawItems?.[0];

          if (firstResult.brandId && firstResult.industryId) {
            brandInfo = {
              brandId: firstResult.brandId,
              categoryId: firstResult.industryId,
              brandName: firstRawResult?.brandName || firstResult.brand,
              categoryName: firstRawResult?.categoryName,
            };
            searchResults = searchResult.rawItems || [];
          } else {
            console.warn("搜索结果缺少brandId或industryId");
            return {
              searchResults: searchResult.rawItems || [],
              models: [],
              brandMetrics: null,
            };
          }
        } else {
          console.log("无搜索结果");
          return {
            searchResults: [],
            models: [],
            brandMetrics: null,
          };
        }
      }

      // 第二步：统一的数据获取逻辑，使用传入的参数
      console.log("📊 获取品牌数据:", brandInfo);
      console.log(
        "品牌ID:",
        brandInfo.brandId,
        "品牌名称:",
        brandInfo.brandName || i18n.t("api.log_unknown_brand")
      );
      console.log(
        "分类ID:",
        brandInfo.categoryId,
        "行业名称:",
        brandInfo.categoryName || i18n.t("api.log_unknown_industry")
      );

      const [
        models,
        rates,
        /*trend,*/ brandComparison,
        aiTrend,
        reputationKeywords,
        mediaCite,
        contentCite,
        brandRates,
      ] = await Promise.all([
        modelsApi.getModelsList(),
        brandMetricsRatesApi.getBrandMetricsRates({
          brandId: brandInfo.brandId,
          categoryId: brandInfo.categoryId,
          days,
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        // brandMetricsTrendApi.getTrend({
        //   brandId: brandInfo.brandId,
        //   categoryId: brandInfo.categoryId,
        //   days,
        //   modelId,
        //   metricType: 'mentionRate,firstMentionRate,firstRecommendationRate,positiveMentionRate,negativeMentionRate'
        // }),
        brandComparisonApi.getComparisonData(
          brandInfo.brandId,
          brandInfo.categoryId,
          days,
          "mentionRate,firstMentionRate,firstRecommendationRate,positiveMentionRate,negativeMentionRate",
          modelId,
          startDate,
          endDate,
          qId
        ),
        brandAiMetricsTrendApi.getAiTrend({
          brandId: brandInfo.brandId,
          categoryId: brandInfo.categoryId,
          days,
          metricType:
            "mentionRate,firstMentionRate,firstRecommendationRate,positiveMentionRate,negativeMentionRate",
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        reputationApi.getReputationKeywords({
          brandId: brandInfo.brandId,
          categoryId: brandInfo.categoryId,
          days,
          metricType: "positive,negative",
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        mediaCiteApi.getMediaCite({
          brandId: brandInfo.brandId,
          categoryId: brandInfo.categoryId,
          days,
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        contentCiteApi.getContentCite({
          brandId: brandInfo.brandId,
          categoryId: brandInfo.categoryId,
          days,
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        brandRatesApi.getBrandRates({
          brandId: brandInfo.brandId,
          categoryId: brandInfo.categoryId,
          days,
          modelId,
          orderRate: "mentionRate",
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
      ]);

      const result: SearchFlowResult = {
        searchResults,
        models,
        brandMetrics: rates,
        //brandMetricsTrend: trend,
        brandComparison: brandComparison,
        brandAiMetricsTrend: aiTrend,
        brandRatesList: brandRates?.list,
        reputationKeywords,
        mediaCite,
        contentCite,
      };

      //补充从品牌对比数据派生的趋势
      if (!result.brandMetricsTrend) {
        const derived = this._deriveTrendFromComparison(brandComparison);
        if (derived) {
          result.brandMetricsTrend = derived;
        }
      }

      console.log("✅ 搜索流程完成:", result);
      console.log("品牌指标数据:", rates);
      // console.log('品牌指标趋势点数:', trend)
      console.log("品牌对比数据:", brandComparison);
      console.log("AI平台趋势(字典):", aiTrend);
      console.log("品牌聚合指标(列表):", result.brandRatesList?.length || 0);
      console.log("品牌关键词:", reputationKeywords);
      console.log("媒体引用条目数:", mediaCite);
      console.log("内容引用条目数:", contentCite);
      return result;
    } catch (error) {
      console.error("❌ 搜索流程失败:", error);
      return {
        searchResults: [],
        models: [],
        brandMetrics: null,
        error:
          error instanceof Error
            ? error.message
            : i18n.t("api.error_search_flow"),
      };
    }
  },

  /**
   * 执行时间范围变更的数据刷新
   * 当用户切换时间范围时，重新拉取相关数据
   * @param params 时间范围变更参数
   * @returns Promise<DataRefreshResult> 刷新结果
   */
  async executeTimeRangeChange(
    params: TimeRangeChangeParams
  ): Promise<DataRefreshResult> {
    const {
      brandId,
      categoryId,
      days,
      modelId = "all",
      brandName,
      categoryName,
      startDate,
      endDate,
      qId,
    } = params;
    console.log("⏰ 开始执行时间范围变更:", {
      brandId,
      categoryId,
      days,
      modelId,
      startDate,
      endDate,
      qId,
      brandName: brandName || i18n.t("api.log_unknown_brand"),
      categoryName: categoryName || i18n.t("api.log_unknown_industry"),
    });

    try {
      // 并行获取所有需要刷新的数据
      const [
        rates,
        /*trend,*/ comparison,
        aiTrend,
        reputationKeywords,
        mediaCite,
        contentCite,
        brandRates,
      ] = await Promise.all([
        brandMetricsRatesApi.getBrandMetricsRates({
          brandId,
          categoryId,
          days,
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        // brandMetricsTrendApi.getTrend({
        //   brandId,
        //   categoryId,
        //   days,
        //   modelId,
        //   metricType: 'mentionRate,firstMentionRate,firstRecommendationRate'
        // }),
        brandComparisonApi.getComparisonData(
          brandId,
          categoryId,
          days,
          "mentionRate,firstMentionRate,firstRecommendationRate,positiveMentionRate,negativeMentionRate",
          modelId,
          startDate,
          endDate,
          qId
        ),
        brandAiMetricsTrendApi.getAiTrend({
          brandId,
          categoryId,
          days,
          metricType: "mentionRate,firstMentionRate,firstRecommendationRate",
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        reputationApi.getReputationKeywords({
          brandId: brandId,
          categoryId: categoryId,
          days,
          metricType: "positive,negative",
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        mediaCiteApi.getMediaCite({
          brandId,
          categoryId,
          days,
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        contentCiteApi.getContentCite({
          brandId,
          categoryId,
          days,
          modelId,
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
        brandRatesApi.getBrandRates({
          brandId,
          categoryId,
          days,
          modelId,
          orderRate: "mentionRate",
          startDay: startDate,
          endDay: endDate,
          qId,
        } as any),
      ]);

      const result: DataRefreshResult = {
        brandMetrics: rates,
        //brandMetricsTrend: trend,
        brandAiMetricsTrend: aiTrend,
        brandRatesList: brandRates?.list,
        reputationKeywords,
        mediaCite,
        contentCite,
      };

      // 补充品牌对比数据
      if (comparison) {
        //BrandComparisonChart
        //CompetitorOverviewSection
        result.brandComparison = comparison;
      }

      //补充从品牌对比数据派生的趋势
      if (!result.brandMetricsTrend) {
        const derived = this._deriveTrendFromComparison(comparison);
        if (derived) {
          result.brandMetricsTrend = derived;
          //SentimentAnalysisSection
        }
      }

      console.log("✅ 时间范围变更完成:", {
        days,
        brandMetrics: !!rates,
        //trendPoints: trend?.length || 0,
        aiTrendMetricKeys: aiTrend ? Object.keys(aiTrend).length : 0,
        brandRatesCount: result.brandRatesList?.length || 0,
        reputationKeywords: !!reputationKeywords,
        mediaCiteItems: mediaCite?.length || 0,
        contentCiteItems: contentCite?.length || 0,
      });

      return result;
    } catch (error) {
      console.error("❌ 时间范围变更失败:", error);
      return {
        brandMetrics: null,
        error:
          error instanceof Error
            ? error.message
            : i18n.t("api.error_time_change"),
      };
    }
  },
};

export default searchFlowApi;
